import { useRef } from 'react';
import { useFlowchartCoreGraphCompaction } from './useFlowchartCoreGraphCompaction';
import { useFlowchartCoreTidyPersistence } from './useFlowchartCoreTidyPersistence';
import { useSnapSavedToElkOnSettle } from './flowchartLayout/useSnapSavedToElkOnSettle';
import { useTidyWhenUnpositioned } from './flowchartLayout/useTidyWhenUnpositioned';

/**
 * Graph positions/edges from ELK + compaction and tidy/auto-layout wiring.
 */
export function useFlowchartCoreGraphAndTidy({
  campaignContext,
  base,
  refs,
}) {
  const {
    tasks,
    nestedSprints,
    statuses,
    localSprint,
    allSprints,
    isMultiCampaignMode,
    onPositionChangeBatch,
    hasActiveFilters,
    onPositionChange,
    campaignId,
    wrapLastUndoAction,
    onApplyTaskPositions,
  } = campaignContext;

  const { interaction } = base;
  const {
    autoPositions,
    useAutoPositions,
    elkPositionsOk,
    measuredElkSettled,
    elkEdgePaths,
    chainInfo,
    nodeDimensions,
    committedElkLayoutVersion,
    triggerLayout,
    triggerLayoutInstant,
    elkPositionsOkRef,
    setAutoLayoutHandler,
    autoPan,
    afterDeleteHandlerRef,
    layoutHoldRef,
  } = base;

  const { autoPositionsRef } = refs;

  const draggingNodeRef = useRef(null);
  draggingNodeRef.current = interaction.draggingNode;

  const {
    pan,
    zoom,
    containerRef,
    setPanAnimated,
    handleAutoLayout: interactionAutoLayout,
  } = interaction;

  const graph = useFlowchartCoreGraphCompaction({
    tasks,
    nestedSprints,
    statuses,
    localSprint,
    allSprints,
    interaction,
    autoPositions,
    useAutoPositions,
    elkPositionsOk,
    elkEdgePaths,
    chainInfo,
    nodeDimensions,
    isMultiCampaignMode,
    onPositionChangeBatch,
    wrapLastUndoAction,
    onApplyTaskPositions,
    hasActiveFilters,
    pan,
    zoom,
    containerRef,
    setPanAnimated,
    autoPan,
    afterDeleteHandlerRef,
    suppressEdgesUntilElkReady: !isMultiCampaignMode && !elkPositionsOk,
  });

  const nodePositionsRef = useRef(graph.nodePositions);
  nodePositionsRef.current = graph.nodePositions;
  autoPositionsRef.current = autoPositions;

  useSnapSavedToElkOnSettle({
    isMultiCampaignMode,
    hasActiveFilters,
    measuredElkSettled,
    useAutoPositions,
    campaignId,
    tasks,
    autoPositions,
    interaction,
    onPositionChangeBatch,
    onPositionChange,
    draggingNodeRef,
    committedElkLayoutVersion,
    layoutHoldRef,
  });

  const { handleAutoLayout, handleAutoLayoutInstant, isAnimatingTidy, isPartialTidyToolbarLoading } =
    useFlowchartCoreTidyPersistence({
    tasks,
    autoPositions,
    campaignId,
    onPositionChange,
    onPositionChangeBatch,
    interaction,
    triggerLayout,
    triggerLayoutInstant,
    interactionAutoLayout,
    isMultiCampaignMode,
    nodeDimensions,
    elkPositionsOkRef,
    compactAnimationStartRef: graph.compactAnimationStartRef,
    compactAnimationEndRef: graph.compactAnimationEndRef,
  });

  setAutoLayoutHandler(handleAutoLayout);

  useTidyWhenUnpositioned({
    tasks,
    campaignId,
    measuredElkSettled,
    useAutoPositions,
    isMultiCampaignMode,
    hasActiveFilters,
    layoutHoldRef,
    draggingNodeRef,
    handleAutoLayout,
  });

  return {
    graph,
    nodePositionsRef,
    handleAutoLayout,
    handleAutoLayoutInstant,
    isAnimatingTidy,
    isPartialTidyToolbarLoading,
  };
}
